import { SITE_URL } from "@/lib/config";
import { Helmet } from "react-helmet-async";

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;

interface PageSEOProps {
    title: string;
    description: string;
    path: string;
    ogImage?: string;
    /** Alt text for the social share image. Defaults to the page title. */
    ogImageAlt?: string;
    type?: "website" | "article";
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    keywords?: string[];
    noIndex?: boolean;
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    /** Sets <html lang>; omit to leave the document default. */
    lang?: string;
    /** Sets <html dir> (use "rtl" for Urdu etc.). */
    dir?: "ltr" | "rtl";
    /** hreflang alternates for localized variants. If set, x-default uses
     *  `xDefaultHref` (falls back to the canonical URL). */
    alternates?: { hrefLang: string; href: string }[];
    xDefaultHref?: string;
}

const PageSEO = ({
    title,
    description,
    path,
    ogImage = DEFAULT_OG_IMAGE,
    ogImageAlt,
    type = "website",
    jsonLd,
    keywords,
    noIndex = false,
    publishedTime,
    modifiedTime,
    author,
    lang,
    dir,
    alternates,
    xDefaultHref,
}: PageSEOProps) => {
    const canonicalUrl = `${SITE_URL}${path}`;
    const shouldNoIndex = noIndex || import.meta.env.VITE_NOINDEX === "true";
    const resolvedOgImageAlt = ogImageAlt ?? title;
    // We only know the exact dimensions/type of the bundled default share image.
    const isDefaultOgImage = ogImage === DEFAULT_OG_IMAGE;

    return (
        <Helmet>
            {lang && <html lang={lang} {...(dir ? { dir } : {})} />}
            <title>{title}</title>
            <meta name="description" content={description} />
            {shouldNoIndex && (
                <meta name="robots" content="noindex, nofollow" />
            )}
            {keywords && keywords.length > 0 && (
                <meta name="keywords" content={keywords.join(", ")} />
            )}
            <link rel="canonical" href={canonicalUrl} />
            {alternates?.map((a) => (
                <link
                    key={a.hrefLang}
                    rel="alternate"
                    hrefLang={a.hrefLang}
                    href={a.href}
                />
            ))}
            <link
                rel="alternate"
                hrefLang="x-default"
                href={xDefaultHref ?? canonicalUrl}
            />

            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:alt" content={resolvedOgImageAlt} />
            {isDefaultOgImage && (
                <meta property="og:image:type" content="image/jpeg" />
            )}
            {isDefaultOgImage && (
                <meta property="og:image:width" content="1200" />
            )}
            {isDefaultOgImage && (
                <meta property="og:image:height" content="630" />
            )}
            <meta property="og:locale" content="en_US" />
            <meta property="og:site_name" content="Pixro AI" />
            {publishedTime && (
                <meta
                    property="article:published_time"
                    content={publishedTime}
                />
            )}
            {modifiedTime && (
                <meta property="article:modified_time" content={modifiedTime} />
            )}
            {author && <meta property="article:author" content={author} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@pixroai" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={resolvedOgImageAlt} />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
};

export default PageSEO;
